import { useMemo, useState } from 'react';
import { Box, makeStyles } from '@material-ui/core';
import _ from 'lodash';
import { AutoSizer, CellMeasurer, CellMeasurerCache, Column, Table } from 'react-virtualized';
import { useSelector } from 'react-redux';
import { classNames, conditional, getUserInfo } from '../../../../../utils';
import BusStopUI from '../../../components/BusStop/components/BusStopUI';
import { i18n } from '../../../../../language';
import { Alert } from '../../../../../components';
import OtherInfo from '../../../components/BusStop/components/OtherInfo';
import { maskChineseName, maskEnglishName } from '../../../../../utils/admissionUtil';
import { useAdmissionVersionError } from '../../../EmergencyAdmission/EAdmissionForm';
import { LoginPop } from '../../../../../components/Login';
import Row from './Row';

const TransferInTable = ({
  data,
  handleScroll,
  openEdit,
  bedstatus,
  handleEditEADM,
  handleUpdateSingleData,
  handleRowDoubleClick,
  type,
  admissionTypeList,
}) => {
  const classes = useStyles();
  const userInfo = getUserInfo();
  const [successAlert, setSuccessAlert] = useState(false);
  const isLogin = useSelector((state) => state.loginInfo.flag);
  const handleAdmissionVersionError = useAdmissionVersionError();
  const [isShowLoginPop, setIsShowLoginPop] = useState(false);

  const isLoginName = (chiName) => {
    return conditional(chiName, `${chiName} `, '');
  };

  const isNotLoginName = (chiName) => {
    return conditional(chiName, `${maskChineseName(chiName)} `, '');
  };

  const tableData = useMemo(() => {
    let tempData = _.map(data, (item) => ({
      ...item,
      patient: {
        caseNo: item.caseNo,
        name: isLogin
          ? `${isLoginName(item.chiName)}${item.patName}`
          : `${isNotLoginName(item.chiName)}${maskEnglishName(item.patName)}`,
        gender: item.patGender,
        age: item.age,
      },
      hold: item?.status === 'Hold',
    }));

    return tempData;
  }, [data, userInfo]);

  // 创建一个 CellMeasurerCache，用于缓存测量结果
  const cache = new CellMeasurerCache({
    defaultHeight: 48, // 默认高度
    fixedWidth: true, // 如果表格的宽度是固定的，可以设置为 true
  });

  const cellRenderer = ({ columnIndex, rowIndex, key, parent }) => {
    const rowData = tableData[rowIndex];
    const cellKey = `${rowData?.eaId}-${rowIndex}-${columnIndex}-${rowData?.caseNo}`;
    // const busStop = columnIndex === 0 && rowData.busStopRow;
    // const otherInfo = rowData.otherInfo;

    if (!cache.has(rowIndex, columnIndex)) {
      cache.clearAll(); // 清除缓存以避免其他测量结果干扰
      cache.has(rowIndex, columnIndex); // 手动执行测量
    }

    return (
      <CellMeasurer cache={cache} columnIndex={columnIndex} key={cellKey} parent={parent} rowIndex={rowIndex}>
        {({ measure }) => renderContent(rowIndex, columnIndex, measure)}
      </CellMeasurer>
    );
  };

  const renderContent = (rowIndex, columnIndex, measure) => {
    const rowData = tableData[rowIndex];
    const busStop = columnIndex === 0 && rowData.busStopRow;
    const otherInfo = rowData.otherInfo;
    const cellKey = `${rowData?.eaId}-${rowIndex}-${columnIndex}-${otherInfo ? 'isOther' : 'notOther'}`;

    if (rowData.busStopRow) {
      if (busStop) {
        return (
          <div key={cellKey} onLoad={measure}>
            {rowData?.admissionType === 'EADM' && (
              <BusStopUI data={rowData?.busStopInfo} busStopStep={rowData?.busStopStep} gender={rowData?.sex} />
            )}
          </div>
        );
      }
      return null; // 如果 busStop 为 false，返回 null
    } else if (otherInfo) {
      if (columnIndex === 0) {
        return (
          <div key={cellKey}>
            <OtherInfo rowData={rowData} />
          </div>
        );
      }
      return null;
    } else {
      return (
        <Box key={cellKey} p="0.5rem 0.5rem 0.5rem 0">
          <Row
            {...rowData}
            showDetail={tableData[rowIndex + 1]?.busStopRow && tableData[rowIndex + 1]?.display}
            columnIndex={columnIndex}
            openEdit={openEdit}
            bedstatus={bedstatus}
            handleEditEADM={handleEditEADM}
            setSuccessAlert={setSuccessAlert}
            handleUpdateSingleData={handleUpdateSingleData}
            handleAdmissionVersionError={handleAdmissionVersionError}
            setIsShowLoginPop={setIsShowLoginPop}
            admissionTypeList={admissionTypeList}
          />
        </Box>
      );
    }
  };

  const getRowClassName = ({ index }) => {
    if (tableData[index]?.otherInfo) {
      return classes.otherInfo;
    } else if (index > 0) {
      return classNames(classes.flexContainer, classes.borderRow);
    } else {
      return classes.flexContainer;
    }
  };

  const columns = [
    {
      label: i18n.t('BEDSTATUS_STATUS'),
      dataKey: 'status',
      widthBasis: 0.075,
    },
    {
      label: i18n.t('BEDSTATUS_ADMISSION_DATE_TABLE'),
      dataKey: 'adt',
      widthBasis: 0.09,
    },
    {
      label: 'Admission Type',
      dataKey: 'adt',
      widthBasis: 0.09,
    },
    {
      label: 'Specialty',
      dataKey: 'eisSpecCd',
      widthBasis: 0.055,
    },
    {
      label: i18n.t('BEDSTATUS_PATIENT'),
      dataKey: 'adt',
      widthBasis: 0.135,
    },
    {
      label: `Loc/${i18n.t('BEDSTATUS_TI_UNIT')}/
      ${i18n.t('BEDSTATUS_BED_NO')}`,
      dataKey: 'adt',
      widthBasis: 0.095,
    },
    {
      label: i18n.t('BEDSTATUS_PRINCIPLE_DIAGNOSIS'),
      dataKey: 'adt',
      widthBasis: 0.085,
    },
    {
      label: i18n.t('BEDSTATUS_REMARK'),
      dataKey: 'adt',
      widthBasis: 0.17,
    },
    {
      label: `Dest. Hosp./unit/ward loc.`,
      dataKey: 'adt',
      widthBasis: 0.105,
    },
    {
      label: `Ward Ready Time`,
      dataKey: 'adt',
      widthBasis: 0.075,
    },
    {
      label: `Adm. Reg. Time`,
      dataKey: 'adt',
      widthBasis: 0.075,
    },
    {
      label: '',
      dataKey: 'adt',
      widthBasis: 0.04,
    },
  ];

  return (
    <div style={{ flex: 1, minHeight: 0 }}>
      <AutoSizer>
        {({ height, width }) => (
          <Table
            height={height}
            width={width}
            rowHeight={cache.rowHeight}
            headerHeight={40}
            rowCount={tableData.length}
            rowGetter={({ index }) => tableData[index]}
            rowClassName={getRowClassName}
            onScroll={handleScroll}
            onRowDoubleClick={handleRowDoubleClick}
          >
            {columns.map(({ dataKey, label, widthBasis }) => {
              return (
                <Column
                  key={label}
                  headerClassName={classes.tableHead}
                  cellRenderer={cellRenderer}
                  label={label}
                  dataKey={dataKey}
                  width={width * widthBasis}
                />
              );
            })}
          </Table>
        )}
      </AutoSizer>
      {/* alert */}
      <Alert
        open={successAlert}
        id="Alert-aib"
        onClose={() => setSuccessAlert(false)}
        text={i18n.t('DUTYASSIGN_SAVE_SUCCESS_ALERT')}
        type="success"
      />
      {isShowLoginPop && (
        <LoginPop
          closehandle={() => setIsShowLoginPop(false)}
          submitHandle={() => setIsShowLoginPop(false)}
          tokenExpiration={true}
        />
      )}
    </div>
  );
};

const useStyles = makeStyles((theme) => ({
  flexContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    boxSizing: 'border-box',
    color: '#2E4469',
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
  },
  borderRow: {
    borderTop: 'solid 1px #AAAAAA',
  },
  tableHead: {
    color: '#538FFF',
    fontFamily: 'NotoSans-Bold',
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    borderBottom: 'none !important',
    // paddingBottom: '1rem !important',
  },
  otherInfo: {
    borderTop: 'unset !important',
  },
}));

export default TransferInTable;
